/**
 * AI Service Hooks
 * React hooks for accessing AI management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all AI-related services
 * @returns AiServices object containing all AI service instances
 */
export function useAiServices() {
    const { services } = useCCMS();
    if (!services?.ai) {
        throw new Error('AI services not available. Make sure CCMSProvider is configured with AI service.');
    }
    return services.ai;
}
/**
 * Hook to access AiFeatureFlagService
 */
export function useAiFeatureFlagService() {
    return useAiServices().featureFlag;
}
/**
 * Hook to access AiWorkflowPresetService
 */
export function useAiWorkflowPresetService() {
    return useAiServices().workflowPreset;
}
/**
 * Hook to access AiRecordSchemaService
 */
export function useAiRecordSchemaService() {
    return useAiServices().recordSchema;
}
/**
 * Hook to access AiRequestLogService
 */
export function useAiRequestLogService() {
    return useAiServices().requestLog;
}
/**
 * Hook to access AiModelService
 */
export function useAiModelService() {
    return useAiServices().model;
}
/**
 * Hook to access AiContentService
 */
export function useAiContentService() {
    return useAiServices().content;
}
/**
 * Hook to access AiExtractionService — generic document extraction.
 * Reads labelled fields off a photographed or scanned document; the caller maps
 * those labels onto its own entity.
 */
export function useAiExtractionService() {
    return useAiServices().extraction;
}
/**
 * Hook to access AiAnalyticsService
 */
export function useAiAnalyticsService() {
    return useAiServices().analytics;
}
/**
 * AI analysis over the budget book. Every figure it returns was computed deterministically by
 * the Budget service before the model saw it — the model narrates, it does not calculate.
 */
export function useAiBudgetAnalyticsService() {
    return useAiServices().budgetAnalytics;
}
/**
 * Hook to access AiWorkflowService
 */
export function useAiWorkflowService() {
    return useAiServices().workflow;
}
/**
 * Hook to access AiDocumentService
 */
export function useAiDocumentService() {
    return useAiServices().document;
}
/**
 * Hook to access AiClassificationService
 */
export function useAiClassificationService() {
    return useAiServices().classification;
}
/**
 * Hook to access AiReportService
 */
export function useAiReportService() {
    return useAiServices().report;
}
//# sourceMappingURL=useAiServices.js.map